import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { WaveDivider } from "@/components/WaveDivider";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { GalleryCarousel } from "@/components/GalleryCarousel";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { useI18n } from "@/lib/i18n";
import { useHeroFade } from "@/hooks/use-hero-fade";

const ctaHome = pic("cta-home");
const workHero = pic("work-hero");
import { pic } from "@/lib/responsive-images";

const heroRoof = pic("hero-roof-solar");
const solarDetail = pic("solar-detail");
const svcRoofing = pic("svc-roofing");
const svcSolar = pic("svc-solar");
const svcCommercial = pic("svc-commercial");
const svcGutters = pic("svc-gutters");
const svcInsurance = pic("svc-insurance");
const svcStorm = pic("svc-storm");
const baShingleBefore = pic("ba-shingle-before");
const baShingleAfter = pic("ba-shingle-after");
const baRepairBefore = pic("ba-repair-before");
const baRepairAfter = pic("ba-repair-after");
const baSolarBefore = pic("ba-solar-before");
const baSolarAfter = pic("ba-solar-after");
const baIntegratedBefore = pic("ba-integrated-before");
const baIntegratedAfter = pic("ba-integrated-after");

export const Route = createFileRoute("/work")({
  head: () => ({
    meta: [
      { title: "Our Work — AMR 3 Design & Construction" },
      {
        name: "description",
        content:
          "Browse real roofing and solar projects completed by AMR 3 Design & Construction across Albuquerque, Rio Rancho, Santa Fe and the rest of New Mexico.",
      },
      { property: "og:title", content: "Our Work — AMR 3 Design & Construction" },
      {
        property: "og:description",
        content:
          "Real roofing, solar and commercial projects across New Mexico — proof of our 25+ years of craftsmanship.",
      },
      { property: "og:image", content: "https://www.amr3designconstruction.com/og-work.jpg" },
      { property: "og:image:secure_url", content: "https://www.amr3designconstruction.com/og-work.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Our Work — AMR 3 Design & Construction" },
      { property: "og:url", content: "https://www.amr3designconstruction.com/work" },
      { name: "twitter:image", content: "https://www.amr3designconstruction.com/og-work.jpg" },
      { name: "twitter:title", content: "Our Work — AMR 3 Design & Construction" },
      { name: "twitter:description", content: "Real roofing, solar and commercial projects across New Mexico." },
    ],
    links: [{ rel: "canonical", href: "https://www.amr3designconstruction.com/work" }],
  }),
  component: WorkPage,
});

const galleryProjects: { src: string; srcSet?: string; alt: string }[] = [
  { src: heroRoof.src, srcSet: heroRoof.srcSet, alt: "Full residential roof replacement with integrated solar in Albuquerque" },
  { src: svcSolar.src, srcSet: svcSolar.srcSet, alt: "Rooftop solar PV array installed on a New Mexico home" },
  { src: svcRoofing.src, srcSet: svcRoofing.srcSet, alt: "Asphalt shingle roof installation in Rio Rancho" },
  { src: solarDetail.src, srcSet: solarDetail.srcSet, alt: "Close-up of solar panel mounting and flashing detail" },
  { src: svcCommercial.src, srcSet: svcCommercial.srcSet, alt: "TPO commercial roof system on a New Mexico business" },
  { src: svcStorm.src, srcSet: svcStorm.srcSet, alt: "Storm-damaged shingles repaired after a hailstorm" },
  { src: svcGutters.src, srcSet: svcGutters.srcSet, alt: "Seamless gutter installation along the roofline" },
  { src: svcInsurance.src, srcSet: svcInsurance.srcSet, alt: "Insurance claim roof inspection and documentation" },
  { src: ctaHome.src, srcSet: ctaHome.srcSet, alt: "Modern New Mexico home featuring AMR 3 roof and solar work" },
];

const beforeAfterPairs: { id: string; title: string; before: string; after: string; beforeSrcSet?: string; afterSrcSet?: string }[] = [
  {
    id: "1",
    title: "Full Asphalt Shingle Replacement",
    before: baShingleBefore.src, beforeSrcSet: baShingleBefore.srcSet,
    after: baShingleAfter.src, afterSrcSet: baShingleAfter.srcSet,
  },
  {
    id: "2",
    title: "Worn Roof to Engineered Repair",
    before: baRepairBefore.src, beforeSrcSet: baRepairBefore.srcSet,
    after: baRepairAfter.src, afterSrcSet: baRepairAfter.srcSet,
  },
  {
    id: "3",
    title: "Bare Roof to High-Efficiency Solar Array",
    before: baSolarBefore.src, beforeSrcSet: baSolarBefore.srcSet,
    after: baSolarAfter.src, afterSrcSet: baSolarAfter.srcSet,
  },
  {
    id: "4",
    title: "Integrated Solar Roofing System",
    before: baIntegratedBefore.src, beforeSrcSet: baIntegratedBefore.srcSet,
    after: baIntegratedAfter.src, afterSrcSet: baIntegratedAfter.srcSet,
  },
];

function WorkPage() {
  const { t, lang } = useI18n();
  const heroRef = useHeroFade<HTMLElement>();
  const { data: sanityItems } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await import("@/lib/sanity")).getGalleryItems(),
  });
  const { data: sanityBeforeAfter } = useQuery({
    queryKey: ["beforeAfter"],
    queryFn: async () => (await import("@/lib/sanity")).getBeforeAfterItems(),
  });
  const carouselItems =
    sanityItems && sanityItems.length > 0
      ? sanityItems.map((g) => ({ src: g.imageUrl, alt: g.alt }))
      : galleryProjects;
  const baItems =
    sanityBeforeAfter && sanityBeforeAfter.length > 0
      ? sanityBeforeAfter.map((b) => ({
          id: b._id,
          title: lang === "es" ? b.title_es : b.title_en,
          before: b.beforeUrl,
          after: b.afterUrl,
          beforeSrcSet: undefined as string | undefined,
          afterSrcSet: undefined as string | undefined,
        }))
      : beforeAfterPairs;



  return (
    <>
      <section ref={heroRef} className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 will-change-[opacity]">
        <img
          src={workHero.src}
          srcSet={workHero.srcSet}
          sizes="100vw"
          alt="Aerial view of completed AMR 3 roofing and solar projects across a New Mexico neighborhood at golden hour"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[oklch(0.18_0.03_50/0.72)] via-[oklch(0.18_0.03_50/0.54)] to-[oklch(0.18_0.03_50/0.34)]" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.work") }]} />
            <p className="mb-4 inline-block rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-accent backdrop-blur">
              {t("work.badge")}
            </p>
            <h1 className="font-serif text-4xl font-bold leading-tight text-white drop-shadow sm:text-5xl lg:text-[3.25rem]">
              {t("work.h1")}
            </h1>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
              <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
                <a href="tel:+15058680847" className="gap-2">
                  <Phone className="h-4 w-4" /> {t("common.callPhone")}
                </a>
              </Button>
              <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
                <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW CAROUSEL */}
      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">
              {t("work.galleryTitle")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("work.gallerySub")}</p>
          </div>
          <GalleryCarousel items={carouselItems} />
        </div>
      </section>

      <WaveDivider topColor="var(--background)" bottomColor="var(--secondary)" />

      {/* BEFORE & AFTER */}
      <section className="bg-secondary py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="font-serif text-3xl font-bold text-primary sm:text-4xl">
              {t("work.baTitle")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("work.baSub")}</p>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
              {lang === "es"
                ? "Cada proyecto comienza con una inspección detallada y fotografías del estado original. Documentamos el daño, explicamos las opciones de materiales adaptadas al clima de Nuevo México (sol intenso, granizo y cambios bruscos de temperatura) y terminamos con una revisión final junto al propietario. Deslice cada imagen para comparar el antes y el después."
                : "Every project starts with a detailed inspection and photos of the original condition. We document the damage, explain material options suited to New Mexico's climate (intense sun, hail and sharp temperature swings) and finish with a final walkthrough with the homeowner. Drag each image to compare the before and after."}
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:gap-10">
            {baItems.map((p) => (
              <article key={p.id}>
                <BeforeAfterSlider
                  before={p.before}
                  beforeSrcSet={p.beforeSrcSet}
                  afterSrcSet={p.afterSrcSet}
                  after={p.after}
                  beforeAlt={`${p.title} — before`}
                  afterAlt={`${p.title} — after`}
                />
                <h3 className="mt-4 font-serif text-lg font-semibold text-primary">{p.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden text-primary-foreground">
        <img
          src={ctaHome.src}
          srcSet={ctaHome.srcSet}
          sizes="100vw"
          alt="Modern New Mexico home with a flawless AMR 3 roof and integrated solar array at sunset"
          width={1920}
          height={1280}
          loading="lazy"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.72)] via-[oklch(0.18_0.03_50/0.54)] to-[oklch(0.18_0.03_50/0.26)]" />
        <div className="absolute left-0 right-0 -top-px z-10">
          <WaveDivider flip color="var(--secondary)" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-24 pt-28 text-center sm:px-6 sm:py-28 sm:pt-32">
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">{t("work.cta.title")}</h2>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Button asChild variant="gold" size="xl" className="w-full sm:w-auto">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-5 w-5" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
